import {
  Button,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { EProcessNames } from "api/enums";
import {
  ParentRowIsNotStageException,
  ParentRowNotFoundException,
} from "api/exceptions/ContractRowException";
import { GreaterThanException } from "api/exceptions/InvalidValueException";
import { UploadIcon } from "assets/icons/CustomSVGIcons";
import AttachmentsMoviment from "components/Attachments/AttachmentsMoviment";
import AdditivesImportFromFile from "components/Importation/AdditivesImportFromFile";
import RadioGroupField from "components/UI/Fields/RadioGroupField";
import { lang } from "i18nLang";
import { useEffect, useState } from "react";
import {
  AccessProfileStore,
  ContractRowMovementStore,
  ContractRowStore,
  ContractStore,
  MessageStore,
  MovimentsAttachmentStore,
} from "store";
import AdditiveQuantityForm from "./AdditiveQuantityForm";
import AdditiveRowForm from "./AdditiveRowForm";
import CancellationMassForm from "./CancellationMassForm";
import CancellationQuantityForm from "./CancellationQuantityForm";
import CancellationRowForm from "./CancellationRowForm";

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: 700,
    marginBottom: "0.5rem",
    color: "#AFCBFF",
  },
  button: { width: "48%" },
  input: {
    "& label": { color: "#BDBDBD !important", textTransform: "uppercase" },
    "& input": { color: "#FFFFFF" },
    "& p": { color: "#BDBDBD !important", fontSize: "0.5rem" },
    "& > div::after": { transform: "scaleX(1)" },
  },
  headerContent: {
    padding: "0.5rem",
    height: "fit-content",
    backgroundColor: "#2E2E2E",
  },
  content: { padding: "0.5rem", height: "100%" },
  bulkButtonIcon: {
    width: 12,
    height: 14,
    marginRight: 5,
  },
  bulkButtonText: {
    marginTop: 2,
  },
}));

const options = [
  {
    value: "additiveRow",
    type: "additive",
  },
  {
    value: "additiveQuantity",
    type: "additive",
  },
  {
    value: "cancellationRow",
    type: "cancellation",
  },
  {
    value: "cancellationQuantity",
    type: "cancellation",
  },
  {
    value: "cancellationMass",
    type: "cancellation",
  },
].map((option) => ({
  ...option,
  label: lang(`addsAndCancels.formsTypes.${option.value}`),
  color: "secondary" as any,
}));

const MenuAddsAndCancels = () => {
  const classes = useStyles();
  const [type, setType] = useState("additive");
  const [formSelected, setFormSelected] = useState("");
  const [data, setData] = useState<any>();
  const [hasError, setHasError] = useState(true);
  const [openImport, setOpenImport] = useState(false);
  const movimentIndex = 0;

  useEffect(() => {
    MovimentsAttachmentStore.reset();
    MovimentsAttachmentStore.addMovimentAttachments([]);
  }, []);

  const handleClickCancel = () => {
    MovimentsAttachmentStore.removeAllUnsavedMovimentAttachments();
    ContractStore.closeLeftSideMenu();
  };

  const handleChangeType = (ev) => {
    setType(ev.target.value);
    setFormSelected("");
    setData(undefined);
    setHasError(true);
  };

  const handleChangeOption = (ev, value: string) => {
    if (
      isAdditive(value) &&
      !AccessProfileStore.validateAccessPermissionTo(
        EProcessNames.GERAR_ADITIVO
      )
    ) {
      MessageStore.error(lang("userProfile.profileDoesNotAllow"));
      return;
    }

    if (
      isCancellation(value) &&
      !AccessProfileStore.validateAccessPermissionTo(
        EProcessNames.GERAR_DISTRATO
      )
    ) {
      MessageStore.error(lang("userProfile.profileDoesNotAllow"));
      return;
    }

    setFormSelected(value);
    setData(undefined);
    setHasError(true);
  };

  function isAdditive(value: string) {
    return options.find((op) => op.value === value)?.type === "additive";
  }

  function isCancellation(value: string) {
    return options.find((op) => op.value === value)?.type === "cancellation";
  }

  const handleChangeData = (newData) => setData(newData);
  const handleBlurInput = (field: string, value: any, isValid: boolean) => {
    setHasError(!isValid);

    // Seleciona linha com o código informado
    if (field === "code") {
      if (formSelected !== "additiveRow") {
        ContractRowStore.selectRowByCode(value);
      } else {
        ContractRowStore.unselectRows();
      }
    }
  };

  const handleClickApply = async () => {
    if (!data) return;
    if (MovimentsAttachmentStore.hasFileBeingUploaded(movimentIndex)) {
      MessageStore.warning(lang("attachments.waitUpload"));
      return;
    }

    MessageStore.setLoading(true);
    executeAction()
      .then((row) => {
        const rows = row && !Array.isArray(row) ? [row] : row;
        return MovimentsAttachmentStore.saveMovimentAttachments(
          movimentIndex,
          rows!
        );
      })
      .then(() => {
        MessageStore.success(lang(`addsAndCancels.messages.success.${type}`));
        handleClickCancel();
      })
      .catch((error) => {
        console.error(error);
        if (error instanceof GreaterThanException) {
          MessageStore.error(
            lang(`addsAndCancels.messages.error.balanceGreaterThanZero`)
          );
          return;
        }

        if (
          error instanceof ParentRowNotFoundException ||
          error instanceof ParentRowIsNotStageException
        ) {
          MessageStore.error(error.message);
          return;
        }

        MessageStore.error(
          lang(`addsAndCancels.messages.error.${type}`, error.message)
        );
      })
      .finally(() => {
        MessageStore.setLoading(false);
      });
  };

  const executeAction = async () => {
    switch (formSelected) {
      case "additiveRow":
        return ContractRowMovementStore.createAdditiveRow({
          ...data,
          structured: data.code,
        });
      case "additiveQuantity":
        return ContractRowMovementStore.createAdditiveQuantity(
          data.code,
          data.quantity
        );
      case "cancellationRow":
        return ContractRowMovementStore.createCancellationRow(data.code);
      case "cancellationQuantity":
        return ContractRowMovementStore.createCancellationQuantity(
          data.code,
          data.quantity
        );
      case "cancellationMass":
        const codes = data.codes.split(";");
        return ContractRowMovementStore.createCancellationMass(codes);
      default:
        return;
    }
  };

  const openImportModal = () => {
    if (
      !AccessProfileStore.validateAccessPermissionTo(
        EProcessNames.GERAR_ADITIVO
      )
    ) {
      MessageStore.error(lang("userProfile.profileDoesNotAllow"));
      return;
    }
    setOpenImport(true);
  };

  const closeImportModal = () => {
    setOpenImport(false);
    handleClickCancel();
  };

  return (
    <>
      <Grid container item className={classes.headerContent}>
        <Typography variant="body2" className={classes.title}>
          {lang("addsAndCancels.title")}
        </Typography>
        <Grid container item justifyContent="space-between">
          <Button
            variant="outlined"
            color="secondary"
            className={classes.button}
            onClick={handleClickCancel}
          >
            {lang("cancel")}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            className={classes.button}
            onClick={handleClickApply}
            disabled={hasError}
          >
            {lang("apply")}
          </Button>
        </Grid>
        <TextField
          select
          name="type"
          label={lang("fields.type")}
          value={type}
          className={classes.input}
          color="secondary"
          onChange={handleChangeType}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{ style: { color: "#FFFFFF" } }}
          margin="normal"
        >
          <MenuItem value={"additive"}>{lang("fields.additive")}</MenuItem>
          <MenuItem value={"cancellation"}>
            {lang("fields.cancellation")}
          </MenuItem>
        </TextField>
      </Grid>
      <Grid
        container
        item
        className={classes.content}
        justifyContent="flex-start"
        alignContent="flex-start"
      >
        {type === "additive" && (
          <Button
            color="secondary"
            variant="outlined"
            onClick={openImportModal}
          >
            <UploadIcon className={classes.bulkButtonIcon} fill="#BDBDBD" />
            <span className={classes.bulkButtonText}>
              {lang("addsAndCancels.addInBulk")}
            </span>
          </Button>
        )}
        <AdditivesImportFromFile open={openImport} onClose={closeImportModal} />
        <RadioGroupField
          name="additiveTypes"
          value={formSelected}
          options={options.filter((op) => op.type === type)}
          onChange={handleChangeOption}
          labelStyle={{ color: "#FFFFFF" }}
          radioStyle={{ color: "#757575" }}
        />
        {formSelected === "additiveRow" && (
          <AdditiveRowForm
            classes={classes}
            onChangeData={handleChangeData}
            onBlurInput={handleBlurInput}
          />
        )}
        {formSelected === "additiveQuantity" && (
          <AdditiveQuantityForm
            classes={classes}
            onChangeData={handleChangeData}
            onBlurInput={handleBlurInput}
          />
        )}
        {formSelected === "cancellationRow" && (
          <CancellationRowForm
            classes={classes}
            onChangeData={handleChangeData}
            onBlurInput={handleBlurInput}
          />
        )}
        {formSelected === "cancellationQuantity" && (
          <CancellationQuantityForm
            classes={classes}
            onChangeData={handleChangeData}
            onBlurInput={handleBlurInput}
          />
        )}
        {formSelected === "cancellationMass" && (
          <CancellationMassForm
            classes={classes}
            onChangeData={handleChangeData}
            onBlurInput={handleBlurInput}
          />
        )}
        <AttachmentsMoviment index={movimentIndex} applyChanges={false} />
      </Grid>
    </>
  );
};

export default MenuAddsAndCancels;
